import { memo, useMemo } from "react";
import { ReactSVG } from "react-svg";
import { classNames, formatCurrency, transformDateToLocalDateString } from "helpers";
import { ActivityLogRow, ActivityLogTypeEnum } from "services/api/fleet/vehicles/activitylogs/types";
import { FleetVehicleDetailFormValues } from "services/api/fleet/vehicles/types";
import Badge from "ui/components/Badge/Badge";
import TableIcon from "ui/components/Table/TableSearch/components/TableIcon";
import ActivityChangeList from "../ActivityChangeList/ActivityChangeList";
import { VehicleHistoryTimelineProps } from "./types";
import { getActivityTypeConfig, getInitials, getChangeCountLabel } from "./consts";

// Jedan redak detalja vozila (label lijevo, vrijednost desno).
const DetailRow: React.FC<{ label: string; value: React.ReactNode; strong?: boolean }> = ({ label, value, strong }) => (
  <div className="flex justify-between text-sm mb-1">
    <span className="text-slate-300">{label}</span>
    <span className={classNames("text-right max-w-[180px] truncate", strong ? "font-bold text-green-400" : "font-medium text-white")}>{value}</span>
  </div>
);

// Prošireni detalji vozila (kao VehicleCell) — prikaz na kartici kreiranja vozila.
const VehicleDetailsBlock: React.FC<{ vehicle: FleetVehicleDetailFormValues }> = ({ vehicle }) => {
  const fullName =
    [vehicle.vehicleManufacturer?.name, vehicle.vehicleModel?.name, vehicle.vehicleType?.name]
      .filter(Boolean)
      .join(" ") || vehicle.name || "";
  const netPrice = vehicle.price?.netPrice ?? 0;
  const sellingPrice = vehicle.price?.sellingPrice ?? 0;

  return (
    <div className="mt-3 rounded-lg bg-slate-800 text-white shadow-xl p-4 max-w-[300px]">
      {vehicle.defaultImage && (
        <div className="mb-3 rounded overflow-hidden">
          <img src={vehicle.defaultImage} alt={fullName} className="w-full h-[120px] object-cover rounded" />
        </div>
      )}
      <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Detalji vozila</div>
      <DetailRow label="Naziv" value={fullName} />
      {vehicle.vinNumber && <DetailRow label="VIN" value={<span className="text-xs">{vehicle.vinNumber}</span>} />}
      {vehicle.registrationPlate && <DetailRow label="Registracija" value={vehicle.registrationPlate} />}
      {vehicle.firstRegistrationDate && (
        <DetailRow label="Prva registracija" value={transformDateToLocalDateString(vehicle.firstRegistrationDate)} />
      )}
      {vehicle.colorName && <DetailRow label="Boja" value={vehicle.colorName} />}
      {vehicle.fuelType && <DetailRow label="Gorivo" value={vehicle.fuelType} />}
      {vehicle.kw != null && vehicle.kw > 0 && (
        <DetailRow label="Snaga" value={`${vehicle.kw} kW${vehicle.hp != null && vehicle.hp > 0 ? ` / ${vehicle.hp} KS` : ""}`} />
      )}
      {vehicle.kmTraveled != null && vehicle.kmTraveled > 0 && (
        <DetailRow label="Kilometraža" value={`${vehicle.kmTraveled.toLocaleString()} km`} />
      )}
      {vehicle.cylinderCapacity && <DetailRow label="Zapremina" value={`${vehicle.cylinderCapacity} ccm`} />}
      {(netPrice > 0 || sellingPrice > 0) && (
        <>
          <div className="border-t border-slate-600 my-2" />
          {netPrice > 0 && <DetailRow label="Neto cijena" value={formatCurrency.format(netPrice)} />}
          {sellingPrice > 0 && <DetailRow label="Prodajna cijena" value={formatCurrency.format(sellingPrice)} strong />}
        </>
      )}
    </div>
  );
};

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

const dayLabel = (d: Date): string => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (dayKey(d) === dayKey(today)) return "Danas";
  if (dayKey(d) === dayKey(yesterday)) return "Jučer";
  return transformDateToLocalDateString(d) ?? "";
};

const timeLabel = (d: Date) => d.toLocaleTimeString("hr-HR", { hour: "2-digit", minute: "2-digit" });

// Kartica jednog zapisa aktivnosti (koristi se s obje strane vremenske crte).
const TimelineCard: React.FC<{ item: ActivityLogRow; vehicle?: FleetVehicleDetailFormValues }> = ({ item, vehicle }) => {
  const cfg = getActivityTypeConfig(item.activityLogType);
  const date = new Date(item.created);
  const changeCount = item.changes?.length ?? 0;

  // Zapis kreiranja baš ovog vozila → prikaži proširene detalje (kao VehicleCell).
  const isVehicleCreate =
    item.activityLogType === ActivityLogTypeEnum.Create && !!vehicle && item.entityId === vehicle.id;

  return (
    <div className="min-w-0 w-full text-left">
      {/* Tko je zapis napravio — iznad kartice, da sadržaj kartice ostane sam zapis. */}
      <div className="flex items-center gap-2 mb-1.5">
        <TableIcon initials={getInitials(item.userFullName)} size="small" />
        <span className="text-xs text-slate-500 min-w-0 truncate">{item.userFullName}</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-md transition-colors">
        <div className="p-3">
          {/* Kategorija (entitet) */}
          {item.entityDescription && (
            <div className="text-xs text-slate-400 mb-1">{item.entityDescription}</div>
          )}

          {/* Naslov, a uz desni rub vrsta zapisa (kreirano / ažurirano) */}
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="text-md font-semibold text-slate-900 min-w-0">{item.comment}</div>
            <span className="flex-shrink-0">
              <Badge text={item.activityLogTypeName} variant={cfg.badgeVariant} height="small" showDot />
            </span>
          </div>

          {/* Prošireni detalji vozila na kartici kreiranja vozila */}
          {isVehicleCreate && <VehicleDetailsBlock vehicle={vehicle!} />}

          {/* Diff promjena */}
          {changeCount > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <ActivityChangeList changes={item.changes!} />
            </div>
          )}
        </div>
        {/* Footer meta */}
        {/* <div className="flex items-center gap-4 text-sm text-slate-400 pt-3 px-3 pb-3 border-t border-slate-200">
          {changeCount > 0 && (
            <span className="flex items-center gap-1">
              <ReactSVG src="/media/icons/edit.svg" className="[&_svg]:w-3.5 [&_svg]:h-3.5 [&_path]:stroke-slate-400" />
              {changeCount} {getChangeCountLabel(changeCount)}
            </span>
          )}
          <span className="flex items-center gap-1 ml-auto">
            <ReactSVG src="/media/icons/calendar.svg" className="[&_svg]:w-3.5 [&_svg]:h-3.5 [&_path]:stroke-slate-400" />
            {transformDateToLocalDateString(date)}
          </span>
        </div> */}
      </div>
    </div>
  );
};

// Datum + vrijeme (nasuprotna strana kartice na vremenskoj crti).
const TimeMeta: React.FC<{ date: Date; align: "left" | "right" }> = ({ date, align }) => (
  <div className={classNames("pt-2 text-xs text-slate-400 whitespace-nowrap", align === "right" ? "text-right" : "text-left")}>
    {transformDateToLocalDateString(date)} · {timeLabel(date)}
  </div>
);

const VehicleHistoryTimeline: React.FC<VehicleHistoryTimelineProps> = ({ data, vehicle }) => {
  // Grupiranje po danu, uz očuvanje redoslijeda (najnovije prvo).
  const groups = useMemo(() => {
    const map = new Map<string, { label: string; items: ActivityLogRow[] }>();
    const order: string[] = [];
    data.forEach((item) => {
      const d = new Date(item.created);
      const key = dayKey(d);
      if (!map.has(key)) {
        map.set(key, { label: dayLabel(d), items: [] });
        order.push(key);
      }
      map.get(key)!.items.push(item);
    });
    return order.map((k) => map.get(k)!);
  }, [data]);

  if (data.length === 0) return null;

  return (
    <div className="max-w-6xl mx-auto pt-2 pb-6">
      {groups.map((group, gi) => (
        <div key={gi} className="mb-1">
          {/* Zaglavlje datuma */}
          <div className="flex items-center justify-center gap-2 mb-4 mt-3">
            <ReactSVG src="/media/icons/calendar.svg" className="[&_svg]:w-3.5 [&_svg]:h-3.5 [&_path]:stroke-slate-400" />
            <span className="text-sm font-semibold text-slate-700">{group.label}</span>
            <span className="text-xs text-slate-400">· {group.items.length} {group.items.length === 1 ? "zapis" : "zapisa"}</span>
          </div>

          {/* Naizmjenične stavke lijevo/desno oko centralne crte */}
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 -translate-x-1/2 border-l-2 border-dashed border-slate-200" />

            {group.items.map((item, idx) => {
              const date = new Date(item.created);
              const isLeft = idx % 2 === 0;
              // Najnoviji zapis je istaknut zeleno — ostali su neutralne točke.
              const isLatest = gi === 0 && idx === 0;

              return (
                <div key={item.id} className="relative flex items-start pb-8 last:pb-1">
                  {/* Lijeva strana */}
                  <div className="w-1/2 pr-8 flex justify-end">
                    {isLeft ? <TimelineCard item={item} vehicle={vehicle} /> : <TimeMeta date={date} align="right" />}
                  </div>

                  {/* Točka na centralnoj crti — prsten s ispunom, bez ikone tipa.
                      Bijeli ring prekriva crtkanu crtu ispod točke. */}
                  <div
                    className={classNames(
                      "absolute left-1/2 top-2 -translate-x-1/2 z-10 w-[18px] h-[18px] rounded-full bg-white border-2 flex items-center justify-center ring-4 ring-white",
                      isLatest ? "border-green-500" : "border-slate-300"
                    )}
                  >
                    <span
                      className={classNames(
                        "w-2 h-2 rounded-full",
                        isLatest ? "bg-green-500" : "bg-slate-300"
                      )}
                    />
                  </div>

                  {/* Desna strana */}
                  <div className="w-1/2 pl-8 flex justify-start">
                    {!isLeft ? <TimelineCard item={item} vehicle={vehicle} /> : <TimeMeta date={date} align="left" />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default memo(VehicleHistoryTimeline);
